// Core module route definitions.
// Source of truth for all non-finance routes. App.tsx iterates enabled modules'
// routes via clientModuleRegistry.filter(enabledIds).flatMap((m) => m.routes).

import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import { ProtectedRoute } from '../../components/auth/ProtectedRoute.js';
import { clientModuleRegistry } from '../registry.js';
import { useAuthStore } from '../../store/index.js';
import { evaluateClientPolicy } from '../../auth/policies.js';

import { DashboardPage } from '../../pages/Dashboard.js';
import { AdminPage } from '../../pages/Admin.js';
import { UsersPage } from '../../pages/admin/UsersPage.js';
import { UserDetailPage } from '../../pages/admin/UserDetailPage.js';
import { OrganizationsPage } from '../../pages/admin/Organizations.js';
import { OrganizationDetailPage } from '../../pages/admin/OrganizationDetailPage.js';
import { NewOrganizationPage } from '../../pages/admin/NewOrganizationPage.js';
import { SystemPage } from '../../pages/admin/system/SystemPage.js';
import { SystemHostPage } from '../../pages/admin/system/SystemHostPage.js';
import { SystemDatabasePage } from '../../pages/admin/system/SystemDatabasePage.js';
import { WorkflowListPage } from '../../pages/admin/WorkflowList.js';
// Lazy — pulls in @xyflow/react (~250KB); keep out of main chunk
const WorkflowBuilderPage = React.lazy(() =>
  import('../../pages/admin/WorkflowBuilder.js').then((m) => ({ default: m.WorkflowBuilderPage })),
);
import { SettingsPage } from '../../pages/settings/index.js';
import { TemplatesPage } from '../../pages/agents/Templates.js';
import { LibrariesPage } from '../../pages/agents/Libraries.js';
import { CatalogReviewPage } from '../../pages/agents/CatalogReview.js';
import { ModelCapabilitiesPage } from '../../pages/agents/ModelCapabilities.js';
import { ModelDetailPage } from '../../pages/agents/ModelDetail.js';
import { ModelRuntimePoliciesPage } from '../../pages/agents/ModelRuntimePolicies.js';
import { NewPolicyPage } from '../../pages/agents/runtime/NewPolicyPage.js';
import { PolicyDetailPage } from '../../pages/agents/runtime/PolicyDetailPage.js';
import { ActiveAgentsPage } from '../../pages/agents/ActiveAgents.js';
import { DecisionsPage } from '../../pages/agents/Decisions.js';
import { ToolsPage } from '../../pages/agents/Tools.js';
import { SkillsPage } from '../../pages/agents/Skills.js';
import { SkillDetailPage } from '../../pages/agents/SkillDetail.js';
import { WorkflowRunsPage } from '../../pages/agents/WorkflowRuns.js';
import { WorkflowRunDetailPage } from '../../pages/agents/WorkflowRunDetail.js';
import { TemplateEditorPage } from '../../pages/agents/TemplateEditor.js';
import { NewTemplatePage } from '../../pages/agents/NewTemplatePage.js';
import { ToolEditorPage } from '../../pages/agents/ToolEditor.js';
import { NewToolPage } from '../../pages/agents/NewToolPage.js';
import IntelSourcesPage from '../../pages/agents/IntelSources.js';
import { NewIntelSourcePage } from '../../pages/agents/intel-sources/NewIntelSourcePage.js';
import { AnalysisTemplateEditorPage } from '../../pages/agents/AnalysisTemplateEditor.js';
import { AnalysisTemplatesPage } from '../../pages/agents/AnalysisTemplates.js';
import { TemplateVersionsPage } from '../../pages/agents/TemplateVersions.js';
import { DashboardView } from '../../pages/dashboards/DashboardView.js';
import { DashboardsList } from '../../pages/dashboards/DashboardsList.js';
import { NewDashboard } from '../../pages/dashboards/NewDashboard.js';
import { PlatformConnectionsPage } from '../../pages/settings/PlatformConnections.js';
import { NewPlatformConnectionPage } from '../../pages/settings/NewPlatformConnection.js';
import { McpConnectionsPage } from '../../pages/admin/McpConnections.js';
import { ToolLibrariesPage } from '../../pages/admin/ToolLibraries.js';
import { DaemonsPage } from '../../pages/admin/Daemons.js';
import { DaemonDetailPage } from '../../pages/admin/daemon/DaemonDetail.js';
import { NewDaemonPage } from '../../pages/admin/daemon/NewDaemonPage.js';
import { EmployeeRosterPage } from '../../pages/workforce/EmployeeRoster.js';
import { EmployeeDetailPage } from '../../pages/workforce/EmployeeDetail.js';
import { WorkforceBenchPage } from '../../pages/workforce/WorkforceBench.js';
import { EmployeeProfilePage } from '../../pages/workforce/WorkforceProfile.js';
import { QualityDashboardPage } from '../../pages/admin/QualityDashboard.js';
import { PromotionCandidatesPage } from '../../pages/admin/PromotionCandidates.js';
import { RuntimeFlagsPage } from '../../pages/admin/RuntimeFlags.js';
import { ObservabilityPage } from '../../pages/admin/ObservabilityPage.js';
import { RunMonitoringPage } from '../../pages/admin/RunMonitoring.js';
import { ActionRequestsPage } from '../../pages/admin/ActionRequests.js';
import { ActionRequestDetailPage } from '../../pages/admin/ActionRequestDetail.js';
import { ImprovementsPage } from '../../pages/admin/Improvements.js';
import { OrgSettingsPage } from '../../pages/admin/OrgSettings.js';
import { ApiKeysPage } from '../../pages/admin/ApiKeys.js';
import { SystemOverviewPage } from '../../pages/help/SystemOverview.js';
import { EntitySummariesPage } from '../../pages/help/EntitySummaries.js';
import { MarketplaceBrowsePage } from '../../pages/marketplace/MarketplaceBrowse.js';
import { MarketplacePublishPage } from '../../pages/marketplace/MarketplacePublish.js';
import { MarketplaceNeedsSetupPage } from '../../pages/marketplace/MarketplaceNeedsSetup.js';
import { MarketplaceUpdatesPage } from '../../pages/marketplace/MarketplaceUpdates.js';
import { EvaluationsPage } from '../../pages/admin/Evaluations.js';
import { ReasoningEvaluationsPage } from '../../pages/admin/ReasoningEvaluations.js';
import { NewEvaluationPage } from '../../pages/admin/NewEvaluationPage.js';
import { NewEvaluationTemplatePage } from '../../pages/admin/NewEvaluationTemplatePage.js';
import { EvaluationTemplatesPage } from '../../pages/admin/EvaluationTemplates.js';
import { ConversationsPage } from '../../pages/agents/Conversations.js';
import { ConversationDetailPage } from '../../pages/agents/ConversationDetail.js';
import { NewConversationPage } from '../../pages/agents/NewConversationPage.js';
import { MemoriesPage } from '../../pages/agents/Memories.js';
import { MemoryDetailPage } from '../../pages/agents/MemoryDetail.js';
import { ArtifactsPage } from '../../pages/agents/Artifacts.js';
import { NewArtifactPage } from '../../pages/agents/NewArtifactPage.js';
import { ArtifactDetailPage } from '../../pages/agents/ArtifactDetail.js';

// Projects (Spec 59)
const ProjectsPage = React.lazy(() => import('../../pages/projects/ProjectsPage.js'));
const NewProjectPage = React.lazy(() => import('../../pages/projects/NewProjectPage.js'));
const ProjectDetailPage = React.lazy(() => import('../../pages/projects/ProjectDetailPage.js'));
const ProjectChangeDetailPage = React.lazy(() => import('../../pages/projects/ProjectChangeDetailPage.js'));

// Tasks (Spec 61)
const TasksPage = React.lazy(() => import('../../pages/tasks/TasksPage.js'));
const NewTaskPage = React.lazy(() => import('../../pages/tasks/NewTaskPage.js'));
const TaskDetailPage = React.lazy(() => import('../../pages/tasks/TaskDetailPage.js'));

const ActionRequestProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const allowed = [
    'CanManageWorkflows',
    'CanManageTasks',
    'CanManageWorkforce',
    'CanManageProjects',
    'CanManageWorkstreams',
  ].some((policy) => evaluateClientPolicy(policy, permissions, organizationPlan));
  if (!allowed) return <Navigate to="/" replace />;

  return <>{children}</>;
};

const ProjectSectionRoute: React.FC = () => {
  const { id, section } = useParams<{ id: string; section?: string }>();
  const panel = clientModuleRegistry
    .all()
    .flatMap((m) => m.projectPanels ?? [])
    .find((p) => p.id === section);
  if (panel?.policy) {
    return (
      <ProtectedRoute policy={panel.policy} redirectTo={`/projects/${id ?? ''}`}>
        <ProjectDetailPage />
      </ProtectedRoute>
    );
  }
  return <ProjectDetailPage />;
};

// ==========================================
// Core route definitions
// ==========================================

/** Param-preserving redirect: <Navigate> does not substitute route params (QG-CLI-PAGES-003). */
const RedirectOrganizationUser: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/organization/users/${id ?? ''}`} replace />;
};

export const coreRoutes: RouteObject[] = [
  // Root
  {
    path: '/',
    element: <DashboardPage />,
  },

  // Dashboards
  {
    path: '/dashboards',
    element: (
      <ProtectedRoute policy="CanViewDashboards">
        <DashboardsList />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboards/new',
    element: (
      <ProtectedRoute policy="CanManageDashboards">
        <NewDashboard />
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboards/:id',
    element: (
      <ProtectedRoute policy="CanViewDashboards">
        <DashboardView />
      </ProtectedRoute>
    ),
  },

  // Settings — any authenticated user
  {
    path: '/settings',
    element: <SettingsPage />,
  },
  {
    path: '/settings/:section',
    element: <SettingsPage />,
  },

  // Marketplace — any authenticated user
  {
    path: '/marketplace',
    element: <MarketplaceBrowsePage />,
  },
  {
    path: '/marketplace/publish',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <MarketplacePublishPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/marketplace/needs-setup',
    element: <MarketplaceNeedsSetupPage />,
  },
  {
    path: '/marketplace/updates',
    element: <MarketplaceUpdatesPage />,
  },

  // Help — internal role + superadmin
  {
    path: '/help/system-overview',
    element: (
      <ProtectedRoute policy="CanViewInternalDocs">
        <SystemOverviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/help/entity-summaries',
    element: (
      <ProtectedRoute policy="CanViewInternalDocs">
        <EntitySummariesPage />
      </ProtectedRoute>
    ),
  },

  // AI Agents — workflows
  {
    path: '/agents/workflows',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <WorkflowListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/workflows/new',
    element: (
      <ProtectedRoute policy="CanManageWorkflows">
        <WorkflowBuilderPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/workflows/:id',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <WorkflowBuilderPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/workflows/:id/edit',
    element: (
      <ProtectedRoute policy="CanManageWorkflows">
        <WorkflowBuilderPage />
      </ProtectedRoute>
    ),
  },

  // AI Agents — templates
  {
    path: '/agents/libraries',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <LibrariesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/models',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ModelCapabilitiesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/models/:slug',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ModelDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/model-runtime-policies',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ModelRuntimePoliciesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/model-runtime-policies/new',
    element: (
      <ProtectedRoute policy="CanManageSystem">
        <NewPolicyPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/model-runtime-policies/:id',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <PolicyDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/libraries/catalog-review',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <CatalogReviewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/skills',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <SkillsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/skills/:slug',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <SkillDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/templates',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <TemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/templates/new',
    element: (
      <ProtectedRoute policy="CanManageAgents">
        <NewTemplatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/templates/:templateSlug',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <TemplateEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/templates/:templateId/versions',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <TemplateVersionsPage />
      </ProtectedRoute>
    ),
  },

  // AI Agents — tools
  {
    path: '/agents/tools/new',
    element: (
      <ProtectedRoute policy="CanManageAgents">
        <NewToolPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/tools/:toolName',
    element: (
      <ProtectedRoute policy="CanManageAgents">
        <ToolEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/tools',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ToolsPage />
      </ProtectedRoute>
    ),
  },

  // AI Agents — active agents, decisions, runs, ML, intel
  {
    path: '/agents/active',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ActiveAgentsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/decisions',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <DecisionsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/runs',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <WorkflowRunsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/runs/:runId',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <WorkflowRunDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/action-requests',
    element: (
      <ActionRequestProtectedRoute>
        <ActionRequestsPage />
      </ActionRequestProtectedRoute>
    ),
  },
  {
    path: '/action-requests/:id',
    element: (
      <ActionRequestProtectedRoute>
        <ActionRequestDetailPage />
      </ActionRequestProtectedRoute>
    ),
  },
  {
    path: '/agents/intel-sources',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <IntelSourcesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/intel-sources/new',
    element: (
      <ProtectedRoute policy="CanManageAgents">
        <NewIntelSourcePage />
      </ProtectedRoute>
    ),
  },

  // Conversations
  {
    path: '/conversations',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ConversationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/conversations/new',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <NewConversationPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/conversations/:id',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <ConversationDetailPage />
      </ProtectedRoute>
    ),
  },

  // Memories (Spec 58)
  {
    path: '/memories',
    element: (
      <ProtectedRoute policy="CanViewMemoriesArtifacts">
        <MemoriesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/memories/:id',
    element: (
      <ProtectedRoute policy="CanViewMemoriesArtifacts">
        <MemoryDetailPage />
      </ProtectedRoute>
    ),
  },

  // Artifacts (Spec 58)
  {
    path: '/artifacts',
    element: (
      <ProtectedRoute policy="CanViewMemoriesArtifacts">
        <ArtifactsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/artifacts/new',
    element: (
      <ProtectedRoute policy="CanCreateMemoriesArtifacts">
        <NewArtifactPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/artifacts/:id',
    element: (
      <ProtectedRoute policy="CanViewMemoriesArtifacts">
        <ArtifactDetailPage />
      </ProtectedRoute>
    ),
  },

  // Projects (Spec 59)
  {
    path: '/projects',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProjectsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/new',
    element: (
      <ProtectedRoute policy="CanManageProjects">
        <NewProjectPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:id',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProjectDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:projectId/workforce',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProtectedRoute policy="CanManageWorkforce">
          <EmployeeRosterPage />
        </ProtectedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:projectId/workforce/:id',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProtectedRoute policy="CanManageWorkforce">
          <EmployeeDetailPage />
        </ProtectedRoute>
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:id/changes/:changeId',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProjectChangeDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/projects/:id/:section',
    element: (
      <ProtectedRoute policy="CanViewProjects">
        <ProjectSectionRoute />
      </ProtectedRoute>
    ),
  },

  // Tasks (Spec 61)
  {
    path: '/tasks',
    element: (
      <ProtectedRoute policy="CanManageTasks">
        <TasksPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/tasks/new',
    element: (
      <ProtectedRoute policy="CanManageTasks">
        <NewTaskPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/tasks/:id',
    element: (
      <ProtectedRoute policy="CanManageTasks">
        <TaskDetailPage />
      </ProtectedRoute>
    ),
  },

  // AI Agents — redirects
  {
    path: '/agents/prompt-templates',
    element: <Navigate to="/agents/templates" replace />,
  },
  {
    // The list was lost in the route-manifest migration (747599c6): this redirected to agent templates, which never
    // listed analysis templates. Restored 2026-10-16.
    path: '/agents/analysis-templates',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <AnalysisTemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/analysis-templates/new',
    element: (
      <ProtectedRoute policy="CanManageAgents">
        <AnalysisTemplateEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/agents/analysis-templates/:id',
    element: (
      <ProtectedRoute policy="CanViewAgents">
        <AnalysisTemplateEditorPage />
      </ProtectedRoute>
    ),
  },

  // Workforce — global (org-wide) employee surfaces. Project-scoped supervision
  // stays under /projects/:projectId/workforce* (above).
  {
    path: '/workforce',
    element: (
      <ProtectedRoute policy="CanManageWorkforce">
        <WorkforceBenchPage />
      </ProtectedRoute>
    ),
  },
  {
    // Hiring starts from shared workforce listings until a dedicated global hire page exists.
    path: '/workforce/new',
    element: <Navigate to="/marketplace?type=workforce" replace />,
  },
  {
    path: '/workforce/:id',
    element: (
      <ProtectedRoute policy="CanManageWorkforce">
        <EmployeeProfilePage />
      </ProtectedRoute>
    ),
  },

  // Admin — superadmin only
  {
    path: '/admin',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <AdminPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/users',
    element: <Navigate to="/organization/users" replace />,
  },
  {
    // QG-CLI-PAGES-003: this used to be <Navigate to="/organization/users/:id" />, which sends
    // the LITERAL string ":id" — react-router does not substitute params in a Navigate target.
    // Every bookmarked /admin/users/123 landed on a page for a user called ":id".
    // Mirrors the working pattern in modules/finance/client/redirects.tsx.
    path: '/admin/users/:id',
    element: <RedirectOrganizationUser />,
  },
  {
    path: '/organization/users',
    element: (
      <ProtectedRoute policy="CanManageOrgMembers">
        <UsersPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/organization/users/:id',
    element: (
      <ProtectedRoute policy="CanManageOrgMembers">
        <UserDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/organizations',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <OrganizationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/organizations/new',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <NewOrganizationPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/organizations/:id',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <OrganizationDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/org-settings',
    element: <Navigate to="/organization/settings" replace />,
  },
  {
    path: '/organization/settings',
    element: (
      <ProtectedRoute policy="CanViewOrg">
        <OrgSettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/api-keys',
    element: <Navigate to="/organization/api-keys" replace />,
  },
  {
    path: '/organization/api-keys',
    element: (
      <ProtectedRoute policy="CanManageWorkerSdkKeys">
        <ApiKeysPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/system',
    element: (
      <ProtectedRoute policy="CanManageSystem">
        <SystemPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/system/hosts/:hostId',
    element: (
      <ProtectedRoute policy="CanManageSystem">
        <SystemHostPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/system/databases/:dbId',
    element: (
      <ProtectedRoute policy="CanManageSystem">
        <SystemDatabasePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/platforms',
    element: <Navigate to="/organization/platforms" replace />,
  },
  {
    path: '/organization/platforms',
    element: (
      <ProtectedRoute policy="CanManageIntegrations">
        <PlatformConnectionsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/organization/platforms/new',
    element: (
      <ProtectedRoute policy="CanManageIntegrations">
        <NewPlatformConnectionPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/mcp-connections',
    element: <Navigate to="/organization/mcp-connections" replace />,
  },
  {
    path: '/organization/mcp-connections',
    element: (
      <ProtectedRoute policy="CanManageIntegrations">
        <McpConnectionsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/tool-libraries',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <ToolLibrariesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/daemons',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <DaemonsPage scope="system" />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/daemons/new',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <NewDaemonPage scope="system" />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/daemons/:id',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <DaemonDetailPage scope="system" />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/quality',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <QualityDashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/evaluations',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <EvaluationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/evaluations/reasoning',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <ReasoningEvaluationsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/evaluations/new',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <NewEvaluationPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/evaluation-templates/new',
    element: (
      <ProtectedRoute policy="CanManageEvaluationTemplates">
        <NewEvaluationTemplatePage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/evaluation-templates',
    element: (
      <ProtectedRoute policy="CanManageEvaluationTemplates">
        <EvaluationTemplatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/promotion-candidates',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <PromotionCandidatesPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/runtime-flags',
    element: (
      <ProtectedRoute policy="CanManageOrgRuntimeFlags">
        <RuntimeFlagsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/observability',
    element: (
      <ProtectedRoute policy="RequireSuperadmin">
        <ObservabilityPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/run-monitoring',
    element: (
      <ProtectedRoute policy="CanManageWorkflows">
        <RunMonitoringPage />
      </ProtectedRoute>
    ),
  },

  // Self-improvement proposals (S7)
  {
    path: '/admin/improvements',
    element: (
      <ProtectedRoute policy="CanViewImprovements">
        <ImprovementsPage />
      </ProtectedRoute>
    ),
  },
];
